import axios from 'axios'
import store from '@/store'
import Vue from 'vue'
import router from '@/router'
import moment from 'moment-timezone'

const client = axios.create({
  json: true,
})
const FRIENDLY_TIME_FORMAT = 'MM/DD/YYYY[ at ]hh:mm A'
const DATE_FORMAT = 'MM/DD/YYYY'

export default {
  async authHeader() {
    const token = await Vue.prototype.$auth.getAccessToken()
    return {
      Authorization: `Bearer ${token}`,
    }
  },
  async defaultErrorFn(error) {
    if (error.response.status === 403) {
      console.info('Attempting to refresh Okta auth...')
      const token = await Vue.prototype.$auth.getUser()
        .catch(() => {
          logout()
        })
      if (!token) {
        logout()
      }
      return
    }
    let errorDetails = ''
    if (error.response && error.response.data) {
      errorDetails = ` -- ${error.response.data.message}`
    }
    Vue.prototype.$alert.error(
      `Unable to complete request -- ${error}${errorDetails}`,
    )
  },
  async execute(method, resource, data, overrideErrorFn, successHandlerFn) {
    // inject the idToken for each request
    let authHeader = await this.authHeader()

    // send along the okta info for development because we don't have an auth proxy
    if (process.env.NODE_ENV === "development") {
      authHeader["X-OKTA-ID"] = store.getters.login
      authHeader["X-OKTA-EMAIL"] = store.getters.login
    }

    successHandlerFn = successHandlerFn ? successHandlerFn : (resp) => resp.data

    return client({
      method,
      url: resource,
      data,
      headers: authHeader,
    })
      .then(successHandlerFn)
      .catch(error => {
        overrideErrorFn ? overrideErrorFn(error) : this.defaultErrorFn(error)
        throw error
      })
  },
  async executeExternal(method, resource, data, overrideErrorFn, successHandlerFn) {
    successHandlerFn = successHandlerFn ? successHandlerFn : (resp) => resp.data
    return client({
      method,
      url: resource,
      data,
    })
    .then(successHandlerFn)
    .catch(error => {
      overrideErrorFn ? overrideErrorFn(error) : this.defaultErrorFn(error)
      throw error
    })
  },
  isEnv(env) {
    const environment = process.env.NODE_ENV || ''
    return environment.trim() === env
  },
  formatSize(size) {
    if (size === null || size === undefined) {
      return '- -'
    } else if (size >= 1024 * 1024) {
      return (size / 1024 / 1024).toFixed(0) + 'MB'
    } else if (size >= 1024) {
      return (size / 1024).toFixed(0) + 'KB'
    }
    return size.toString() + ' B'
  },
  formatMIME(mime) {
    if (mime === 'image/jpeg') {
      return 'JPG'
    } else if (mime === 'image/png') {
      return 'PNG'
    } else if (mime === 'application/pdf') {
      return 'PDF'
    } else if (mime === 'image/svg+xml') {
      return 'SVG'
    } else if (mime === 'folder') {
      return 'Folder'
    }
    return mime
  },
  formatDateTime(date, timeZone = 'America/New_York') {
    return moment(date).tz(timeZone).format(FRIENDLY_TIME_FORMAT)
  },
  formatDate(date, timeZone = 'America/New_York') {
    return moment(date).tz(timeZone).format(DATE_FORMAT)
  },
  capitalize(str) {
    if (!str) {
      return ''
    }

    const value = str.toString()
    return value.charAt(0).toUpperCase() + value.slice(1)
  },
  downloadFile(url, fileName) {
    // See https://thewebtier.com/snippets/download-files-with-axios/
    axios({
      url: url,
      method: 'GET',
      responseType: 'blob',
    }).then(response => {
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
    })
  },
}

function logout() {
  console.warn('User session has expired. Logging out.')
  store.dispatch('logout')
  router.push('/login')
}
